import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PersonaCopy } from '../data/personaCopy';
import { CheckCircle, Share2, Twitter, Facebook, Linkedin, Copy, Mail } from 'lucide-react';
import Footer from './Footer';

interface SuccessStateProps {
  copy: PersonaCopy;
  onShare: () => void;
  onTermsClick: () => void;
  onPrivacyClick: () => void;
  referralData?: {
    referralLink: string;
  };
}

const SuccessState: React.FC<SuccessStateProps> = ({ copy, onShare, onTermsClick, onPrivacyClick, referralData }) => {
  const [copied, setCopied] = useState(false);
  const [shareMethod, setShareMethod] = useState<string>('');

  const handleCopyLink = async () => {
    try {
      const linkToCopy = referralData?.referralLink || 'https://quellect.ai';
      await navigator.clipboard.writeText(linkToCopy);
      setCopied(true);
      setShareMethod('copy');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleSocialShare = (platform: string) => {
    const urlToShare = referralData?.referralLink || 'https://quellect.ai';
    setShareMethod(platform);
    const url = encodeURIComponent(urlToShare);
    const text = encodeURIComponent('I just joined the Quellect waitlist! Turn your saved content into searchable knowledge.');
    
    let shareUrl = '';
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=Join Quellect Waitlist&body=${text}%0A%0A${url}`;
        break;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900 flex items-center justify-center p-4 relative" style={{position: 'relative', zIndex: 9999}}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, type: "spring" }}
        className="max-w-4xl w-full"
      >
        {/* Success Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2, type: "spring" }}
            className="w-24 h-24 bg-green-100 rounded-full mx-auto mb-6 flex items-center justify-center"
          >
            <CheckCircle className="w-12 h-12 text-green-600" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-4xl sm:text-5xl font-bold text-white mb-4"
          >
            {copy.microcopy.success.title}
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-xl text-white/80 max-w-2xl mx-auto leading-relaxed"
          >
            {copy.microcopy.success.message}
          </motion.p>
        </div>

        {/* Share Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="card text-center"
        >
          <div className="mb-8 text-center space-y-4">
            <Share2 className="w-16 h-16 text-primary-600 mx-auto" />
            
            <div className="space-y-3">
              <h2 className="text-3xl font-bold text-gray-900 leading-tight max-w-2xl mx-auto">
                {referralData?.referralLink ? 
                  "Here's a gift for helping shape Quellect!" :
                  "Share the excitement!"
                }
              </h2>
              
              <p className="text-xl text-gray-700 font-medium">
                {referralData?.referralLink ? (
                  <>
                    Move up <span className="text-primary-600 font-bold">2 spots</span> per referral
                  </>
                ) : (
                  "Help others discover Quellect and join the waitlist!"
                )}
              </p>
            </div>
          </div>

          {/* Copy Link */}
          <div className="flex items-center space-x-3 max-w-md mx-auto mb-6">
            <input
              type="text"
              value={referralData?.referralLink || 'https://quellect.ai'}
              readOnly
              className="flex-1 form-input text-sm text-center"
            />
            <button
              onClick={handleCopyLink}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                copied && shareMethod === 'copy'
                  ? 'bg-green-500 text-white'
                  : 'bg-primary-600 hover:bg-primary-700 text-white'
              }`}
            >
              {copied && shareMethod === 'copy' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <Copy className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* Share Buttons */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <button
              onClick={() => handleSocialShare('twitter')}
              className="flex items-center justify-center space-x-2 p-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
            >
              <Twitter className="w-5 h-5" />
              <span className="hidden sm:inline">Twitter</span>
            </button>
            
            <button
              onClick={() => handleSocialShare('facebook')}
              className="flex items-center justify-center space-x-2 p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <Facebook className="w-5 h-5" />
              <span className="hidden sm:inline">Facebook</span>
            </button>
            
            <button
              onClick={() => handleSocialShare('linkedin')}
              className="flex items-center justify-center space-x-2 p-3 bg-blue-700 hover:bg-blue-800 text-white rounded-lg transition-colors"
            >
              <Linkedin className="w-5 h-5" />
              <span className="hidden sm:inline">LinkedIn</span>
            </button>
            
            <button
              onClick={() => handleSocialShare('email')}
              className="flex items-center justify-center space-x-2 p-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              <Mail className="w-5 h-5" />
              <span className="hidden sm:inline">Email</span>
            </button>
          </div>

          {copied && shareMethod === 'copy' && (
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-green-600 text-sm mt-2"
            >
              Link copied to clipboard!
            </motion.p>
          )}

          {/* Referral Policy Link */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.9 }}
            className="text-center mt-6"
          >
            <a
              href="/referral-policy"
              className="text-primary-600 hover:text-primary-700 text-sm underline"
            >
              View Referral Code Policy
            </a>
          </motion.div>
        </motion.div>

        {/* Footer */}
        <Footer onTermsClick={onTermsClick} onPrivacyClick={onPrivacyClick} />
      </motion.div>
    </div>
  );
};

export default SuccessState;
